import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import type { ApiClient } from '../api/client';
import ErrorBanner from '../components/ErrorBanner';
import ExecutorTerminalIdentitiesPanel from '../components/ExecutorTerminalIdentitiesPanel';
import type {
  ExecutorAuditEvent,
  ExecutorOperationsResponse,
  ExecutorWorkload,
  TriggerWorkerHelperResponse,
  WorkerHelperJobSummary,
  WorkerNode,
  WorkerToken
} from '../types';
import { reportErrorLocal } from '../utils/errors';
import './ExecutorDetailPage.lane49.css';

type Props = {
  apiClient: ApiClient;
  setError: (message: string | null) => void;
  canOperate: boolean;
  canManageTerminalIdentities: boolean;
};

type ExecutorClonePlan = {
  enrollment_token: string;
  enrollment_token_expires_at: string;
  install_command: string;
  install_paths: Record<string, string>;
};

const EXECUTOR_HEARTBEAT_STALE_AFTER_SECONDS = 120;
const SELF_UPDATE_STATUS_WATCH_MS = 20 * 60 * 1000;
const EXECUTOR_STATUS_REFRESH_MS = 30 * 1000;

type HeartbeatSnapshot = {
  ageSeconds: number;
  observedAt: number;
};

type UpdateWatch = {
  until: number;
  waitingForReconnect: boolean;
};

type LoadExecutorOptions = {
  foreground?: boolean;
};

type ExecutorTab = 'overview' | 'identities' | 'operations' | 'workloads' | 'events';

function formatTimestamp(value?: string | null) {
  return value ? new Date(value).toLocaleString() : '-';
}

function capabilityEnabled(value: unknown): boolean {
  return value === true;
}

function statusClass(status: string) {
  const normalized = status.toLowerCase();
  if (normalized === 'online') return 'badge badge--success';
  if (normalized === 'busy') return 'badge badge--processing';
  if (normalized === 'stale') return 'badge badge--error';
  if (normalized === 'offline') return 'badge';
  return 'badge badge--warning';
}

function monotonicNow() {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function heartbeatAgeSeconds(node: WorkerNode, snapshot?: HeartbeatSnapshot, now = monotonicNow()) {
  if (snapshot) {
    return Math.max(0, Math.floor(snapshot.ageSeconds + (now - snapshot.observedAt) / 1000));
  }
  if (typeof node.heartbeat_age_seconds === 'number' && Number.isFinite(node.heartbeat_age_seconds)) {
    return Math.max(0, Math.round(node.heartbeat_age_seconds));
  }
  const lastSeen = new Date(node.last_seen_at).getTime();
  if (Number.isFinite(lastSeen)) return Math.max(0, Math.floor((Date.now() - lastSeen) / 1000));
  return null;
}

function heartbeatFresh(node: WorkerNode, snapshot?: HeartbeatSnapshot, now = monotonicNow()) {
  const age = heartbeatAgeSeconds(node, snapshot, now);
  return age !== null ? age <= EXECUTOR_HEARTBEAT_STALE_AFTER_SECONDS : node.heartbeat_fresh === true;
}

function statusLabel(node: WorkerNode, fresh: boolean) {
  return node.status.toLowerCase() === 'online' && !fresh ? 'stale' : node.status;
}

function heartbeatHint(node: WorkerNode, snapshot?: HeartbeatSnapshot, now = monotonicNow()) {
  const age = heartbeatAgeSeconds(node, snapshot, now);
  if (age === null) return 'heartbeat age unknown';
  if (age < 60) return `last check-in ${age}s ago`;
  const minutes = Math.floor(age / 60);
  return `last check-in ${minutes}m ${age % 60}s ago`;
}

function runnerStatus(node: WorkerNode, awaitingReconnect: boolean) {
  if (node.runner_current) return { label: 'current', className: 'badge badge--success' };
  const updateStatus = node.self_update?.status?.toLowerCase();
  if (updateStatus === 'queued') return { label: 'update queued', className: 'badge badge--processing' };
  if (updateStatus === 'running') return { label: 'updating', className: 'badge badge--processing' };
  if (updateStatus === 'succeeded') {
    return {
      label: awaitingReconnect ? 'awaiting reconnect' : 'outdated; last update succeeded',
      className: 'badge badge--warning'
    };
  }
  if (updateStatus === 'failed' || updateStatus === 'canceled') {
    return { label: `update ${updateStatus}`, className: 'badge badge--error' };
  }
  return { label: 'outdated', className: 'badge badge--warning' };
}

function helperStatusClass(status?: string | null) {
  const normalized = (status || '').toLowerCase();
  if (normalized === 'succeeded') return 'badge badge--success';
  if (normalized === 'failed' || normalized === 'canceled') return 'badge badge--error';
  if (normalized === 'queued' || normalized === 'running') return 'badge badge--processing';
  return 'badge';
}

function selfUpdateActive(node: WorkerNode) {
  const status = node.self_update?.status?.toLowerCase();
  return status === 'queued' || status === 'running';
}

function optimisticSelfUpdate(
  response: TriggerWorkerHelperResponse,
  previous?: WorkerHelperJobSummary | null
): WorkerHelperJobSummary {
  const now = new Date().toISOString();
  return {
    id: response.id,
    helper: response.helper,
    status: response.status,
    created_at: previous?.id === response.id ? previous.created_at : now,
    leased_at: previous?.id === response.id
      ? previous.leased_at
      : response.status.toLowerCase() === 'running' ? now : null,
    finished_at: previous?.id === response.id ? previous.finished_at : null,
    last_error: previous?.id === response.id ? previous.last_error : null,
    result: previous?.id === response.id ? previous.result : null
  };
}

function requestDuration(createdAt: string, leasedAt?: string | null, finishedAt?: string | null) {
  const start = new Date(leasedAt || createdAt).getTime();
  const end = finishedAt ? new Date(finishedAt).getTime() : Date.now();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return '-';
  const seconds = Math.round((end - start) / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ${seconds % 60}s`;
}

function executorOwnerLabel(node: WorkerNode) {
  if (node.access_level === 'terminal') return 'Shared terminal access';
  if (!node.owner) return 'System managed';
  return node.owner.name?.trim() || node.owner.email?.trim() || `User ${node.owner.id.slice(0, 8)}`;
}

export default function ExecutorDetailPage({
  apiClient,
  setError,
  canOperate,
  canManageTerminalIdentities
}: Props) {
  const { executorId } = useParams<{ executorId: string }>();
  const [node, setNode] = useState<WorkerNode | null>(null);
  const [name, setName] = useState('');
  const [qaPriority, setQaPriority] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [cloneStage, setCloneStage] = useState('');
  const [cloneInstance, setCloneInstance] = useState('');
  const [clonePlan, setClonePlan] = useState<ExecutorClonePlan | null>(null);
  const [updateWatch, setUpdateWatch] = useState<UpdateWatch | null>(null);
  const updateWatchRef = useRef<UpdateWatch | null>(null);
  const heartbeatSnapshot = useRef<HeartbeatSnapshot | null>(null);
  const nameDirty = useRef(false);
  const qaPriorityDirty = useRef(false);
  const nodeRequestVersion = useRef(0);
  const foregroundRequestVersion = useRef<number | null>(null);
  const [heartbeatClock, setHeartbeatClock] = useState(() => monotonicNow());
  const [activeTab, setActiveTab] = useState<ExecutorTab>('overview');
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState<string | null>(null);
  const [workloads, setWorkloads] = useState<ExecutorWorkload[]>([]);
  const [events, setEvents] = useState<ExecutorAuditEvent[]>([]);
  const [operations, setOperations] = useState<ExecutorOperationsResponse | null>(null);
  const canManageNode = canOperate && node?.access_level !== 'terminal';
  const canViewActivity = node?.access_level !== 'terminal';

  const loadNode = useCallback(async ({ foreground = false }: LoadExecutorOptions = {}) => {
    if (!executorId) return;
    const requestVersion = ++nodeRequestVersion.current;
    if (foreground) {
      foregroundRequestVersion.current = requestVersion;
      setLoading(true);
    }
    try {
      const row = await apiClient.get<WorkerNode>(`/api/executors/nodes/${encodeURIComponent(executorId)}`);
      if (requestVersion !== nodeRequestVersion.current) return;
      const observedAt = monotonicNow();
      const age = typeof row.heartbeat_age_seconds === 'number' && Number.isFinite(row.heartbeat_age_seconds)
        ? Math.max(0, row.heartbeat_age_seconds)
        : null;
      heartbeatSnapshot.current = age === null ? null : { ageSeconds: age, observedAt };
      setNode(row);
      if (!nameDirty.current) setName(row.name || '');
      if (!qaPriorityDirty.current) {
        setQaPriority(row.runtime_validation_priority == null ? '' : String(row.runtime_validation_priority));
      }
      const updateStatus = row.self_update?.status?.toLowerCase();
      const watchedUpdate = updateWatchRef.current;
      const updateDelivered = Boolean(
        watchedUpdate?.waitingForReconnect &&
        (row.runner_current || (updateStatus && updateStatus !== 'queued'))
      );
      if (updateDelivered) {
        setMessage(
          row.runner_current
            ? 'Executor reconnected and the self-update completed.'
            : updateStatus === 'running'
              ? 'Executor reconnected and the self-update is running.'
              : updateStatus === 'succeeded'
                ? 'Executor self-update finished; it still reports an older runner.'
                : updateStatus === 'failed'
                  ? 'Executor reconnected, but the self-update failed.'
                  : 'Executor reconnected and accepted the self-update.'
        );
        setUpdateWatch((current) => {
          const next = current ? { ...current, waitingForReconnect: false } : current;
          updateWatchRef.current = next;
          return next;
        });
      }
      setLocalError(null);
    } catch (err) {
      if (requestVersion !== nodeRequestVersion.current) return;
      reportErrorLocal('Failed to load executor', err, setError, setLocalError);
    } finally {
      if (foregroundRequestVersion.current === requestVersion) {
        foregroundRequestVersion.current = null;
        setLoading(false);
      }
    }
  }, [apiClient, executorId, setError]);

  useEffect(() => {
    nameDirty.current = false;
    qaPriorityDirty.current = false;
    heartbeatSnapshot.current = null;
    updateWatchRef.current = null;
    setUpdateWatch(null);
    nodeRequestVersion.current += 1;
    setName('');
    setQaPriority('');
  }, [executorId]);

  useEffect(() => {
    if (!executorId || !['workloads', 'events', 'operations'].includes(activeTab)) return;
    let canceled = false;
    setActivityLoading(true);
    setActivityError(null);
    const load = async () => {
      try {
        if (activeTab === 'workloads') {
          const response = await apiClient.get<ExecutorWorkload[]>(
            `/api/executors/nodes/${encodeURIComponent(executorId)}/workloads`
          );
          if (!canceled) setWorkloads(response);
        } else if (activeTab === 'events') {
          const response = await apiClient.get<ExecutorAuditEvent[]>(
            `/api/executors/nodes/${encodeURIComponent(executorId)}/events`
          );
          if (!canceled) setEvents(response);
        } else if (canManageNode) {
          const response = await apiClient.get<ExecutorOperationsResponse>(
            `/api/executors/nodes/${encodeURIComponent(executorId)}/operations`
          );
          if (!canceled) setOperations(response);
        }
      } catch (err) {
        if (!canceled) {
          reportErrorLocal(`Failed to load executor ${activeTab}`, err, setError, setActivityError);
        }
      } finally {
        if (!canceled) setActivityLoading(false);
      }
    };
    void load();
    return () => {
      canceled = true;
    };
  }, [activeTab, apiClient, canManageNode, executorId, setError]);

  useEffect(() => {
    void loadNode({ foreground: true });
  }, [loadNode]);

  useEffect(() => {
    const handle = window.setInterval(() => setHeartbeatClock(monotonicNow()), 1000);
    return () => window.clearInterval(handle);
  }, []);

  useEffect(() => {
    const handle = window.setInterval(() => void loadNode(), EXECUTOR_STATUS_REFRESH_MS);
    return () => window.clearInterval(handle);
  }, [loadNode]);

  useEffect(() => {
    const watchPending = Boolean(updateWatch && Date.now() < updateWatch.until && node && !node.runner_current);
    if (!node || (!selfUpdateActive(node) && !node.helper_requests?.some((request) => request.status === 'queued' || request.status === 'running') && !watchPending)) return;
    const handle = window.setInterval(() => {
      if (updateWatch && Date.now() >= updateWatch.until) {
        updateWatchRef.current = null;
        setUpdateWatch(null);
        return;
      }
      void loadNode();
    }, 2500);
    return () => window.clearInterval(handle);
  }, [loadNode, node, updateWatch]);

  const heartbeatFreshFor = useCallback(
    (current: WorkerNode) => heartbeatFresh(current, heartbeatSnapshot.current || undefined, heartbeatClock),
    [heartbeatClock]
  );

  const heartbeatHintFor = useCallback(
    (current: WorkerNode) => heartbeatHint(current, heartbeatSnapshot.current || undefined, heartbeatClock),
    [heartbeatClock]
  );

  const updateNode = async (
    payload: Partial<Pick<WorkerNode, 'enabled' | 'conversation_enabled' | 'runtime_validation_priority'>> & {
      name?: string;
    }
  ) => {
    if (!executorId) return;
    nodeRequestVersion.current += 1;
    setSaving(true);
    setMessage(null);
    try {
      const updated = await apiClient.patch<WorkerNode>(
        `/api/executors/nodes/${encodeURIComponent(executorId)}`,
        payload
      );
      nodeRequestVersion.current += 1;
      setNode(updated);
      if (payload.name !== undefined) {
        nameDirty.current = false;
        setName(updated.name || '');
      } else if (!nameDirty.current) {
        setName(updated.name || '');
      }
      if (payload.runtime_validation_priority !== undefined) {
        qaPriorityDirty.current = false;
        setQaPriority(
          updated.runtime_validation_priority == null
            ? ''
            : String(updated.runtime_validation_priority)
        );
      } else if (!qaPriorityDirty.current) {
        setQaPriority(
          updated.runtime_validation_priority == null
            ? ''
            : String(updated.runtime_validation_priority)
        );
      }
      setMessage('Executor settings saved.');
      setLocalError(null);
    } catch (err) {
      reportErrorLocal('Failed to update executor', err, setError, setLocalError);
    } finally {
      setSaving(false);
    }
  };

  const saveName = async () => {
    await updateNode({ name });
  };

  const saveQaPriority = async () => {
    const trimmed = qaPriority.trim();
    if (trimmed === '') {
      await updateNode({ runtime_validation_priority: null });
      return;
    }
    const parsed = Number(trimmed);
    if (!Number.isInteger(parsed) || parsed < 1 || parsed > 9999) {
      setLocalError('QA runtime validation priority must be a whole number between 1 and 9999, or blank.');
      return;
    }
    await updateNode({ runtime_validation_priority: parsed });
  };

  const updateRuntimeValidationTrust = async (trusted: boolean) => {
    if (!node?.token_id) return;
    if (trusted && !window.confirm(
      'Trust this executor token for privileged Firecracker runtime validation? Every executor enrolled with this token inherits the permission.'
    )) {
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      const token = await apiClient.patch<WorkerToken>(
        `/api/executors/tokens/${encodeURIComponent(node.token_id)}`,
        { runtime_validation_trusted: trusted }
      );
      setNode((current) => current ? {
        ...current,
        tags: token.tags,
        runtime_validation_trusted: token.runtime_validation_trusted
      } : current);
      setMessage(
        trusted
          ? 'Executor token trusted for Firecracker runtime validation. Live routing was updated immediately.'
          : 'Firecracker runtime-validation trust removed from the executor token.'
      );
      setLocalError(null);
    } catch (err) {
      reportErrorLocal('Failed to update executor token trust', err, setError, setLocalError);
    } finally {
      setSaving(false);
    }
  };

  const drainAndUpdate = async () => {
    if (!executorId || !node) return;
    setUpdating(true);
    setMessage(null);
    try {
      const response = await apiClient.post<TriggerWorkerHelperResponse>(
        `/api/executors/nodes/${encodeURIComponent(executorId)}/self-update`,
        { payload: { source: 'detail_ui' }, drain_before_update: true }
      );
      const responseStatus = response.status.toLowerCase();
      setNode((current) => current
        ? { ...current, self_update: optimisticSelfUpdate(response, current.self_update) }
        : current);
      const reconnectPending = responseStatus === 'queued' && !heartbeatFreshFor(node);
      const watch = {
        until: Date.now() + SELF_UPDATE_STATUS_WATCH_MS,
        waitingForReconnect: reconnectPending
      };
      updateWatchRef.current = watch;
      setUpdateWatch(watch);
      const liveness = reconnectPending
        ? ` The executor has not checked in for ${heartbeatHintFor(node)}; it cannot consume the request until it reconnects.`
        : '';
      const delivery = reconnectPending ? 'queued until reconnect' : response.status;
      setMessage(`Self-update ${delivery}; conversation processing is drained while the update runs.${liveness}`);
      await loadNode();
    } catch (err) {
      reportErrorLocal('Failed to queue executor self-update', err, setError, setLocalError);
    } finally {
      setUpdating(false);
    }
  };

  const forceUpdate = async () => {
    if (!executorId || !node) return;
    setUpdating(true);
    setMessage(null);
    try {
      const response = await apiClient.post<TriggerWorkerHelperResponse>(
        `/api/executors/nodes/${encodeURIComponent(executorId)}/self-update`,
        { payload: { source: 'detail_ui_force' }, drain_before_update: false }
      );
      const responseStatus = response.status.toLowerCase();
      setNode((current) => current
        ? { ...current, self_update: optimisticSelfUpdate(response, current.self_update) }
        : current);
      const reconnectPending = responseStatus === 'queued' && !heartbeatFreshFor(node);
      const watch = {
        until: Date.now() + SELF_UPDATE_STATUS_WATCH_MS,
        waitingForReconnect: reconnectPending
      };
      updateWatchRef.current = watch;
      setUpdateWatch(watch);
      const liveness = reconnectPending
        ? ` The executor has not checked in for ${heartbeatHintFor(node)}; it cannot consume the request until it reconnects.`
        : '';
      const delivery = reconnectPending ? 'queued until reconnect' : response.status;
      setMessage(`Self-update ${delivery}.${liveness}`);
      await loadNode();
    } catch (err) {
      reportErrorLocal('Failed to queue executor self-update', err, setError, setLocalError);
    } finally {
      setUpdating(false);
    }
  };

  const createClonePlan = async () => {
    if (!executorId) return;
    setSaving(true); setClonePlan(null);
    try {
      const plan = await apiClient.post<ExecutorClonePlan>(
        `/api/executors/nodes/${encodeURIComponent(executorId)}/clone-plan`,
        { target_stage: cloneStage, instance_name: cloneInstance, backend_url: window.location.origin }
      );
      setClonePlan(plan); setLocalError(null);
    } catch (err) { reportErrorLocal('Failed to create independent executor plan', err, setError, setLocalError); }
    finally { setSaving(false); }
  };

  const awaitingReconnect = Boolean(
    updateWatch &&
    updateWatch.waitingForReconnect &&
    Date.now() < updateWatch.until &&
    node &&
    !node.runner_current
  );
  const runtimeStatus = node ? runnerStatus(node, awaitingReconnect) : null;

  return (
    <section className="stack executor-detail49" aria-label="Executor detail">
      <div className="card full executor-detail49__header">
        <div className="card-header">
          <div>
            <Link to="/executors" className="text-small">Back to executors</Link>
            <h2>{node?.name || node?.id || 'Executor'}</h2>
            <p className="status">
              Inspect runtime state, controlled operations, workloads, and audit history.
            </p>
          </div>
          <button
            type="button"
            className="secondary"
            onClick={() => void loadNode({ foreground: true })}
            disabled={loading}
            aria-busy={loading}
          >
            {loading ? 'Refreshing…' : 'Refresh status'}
          </button>
        </div>
        {localError && <div role="alert"><ErrorBanner message={localError} onDismiss={() => setLocalError(null)} /></div>}
        {message && <p className="status status-text--ok" role="status">{message}</p>}
        {node && (
          <div className="tabs executor-detail-tabs" role="group" aria-label="Executor sections">
            {([
              ['overview', 'Overview'],
              ...(canManageTerminalIdentities ? [['identities', 'Identities']] : []),
              ...(canManageNode ? [['operations', 'Operations']] : []),
              ...(canViewActivity ? [['workloads', 'Workloads'], ['events', 'Events']] : [])
            ] as Array<[ExecutorTab, string]>).map(([id, label]) => (
              <button
                key={id}
                type="button"
                aria-pressed={activeTab === id}
                className={activeTab === id ? 'secondary active' : 'secondary'}
                onClick={() => setActiveTab(id)}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      {!node && (
        <div className="card full" role="status">
          {loading ? 'Loading executor details…' : localError
            ? 'Executor details could not be loaded. Use Refresh status to try again.'
            : 'No executor details available.'}
        </div>
      )}
      {node && ['operations', 'workloads', 'events'].includes(activeTab) && (
        <>
          {activityLoading && (
            <p className="status" role="status">
              Loading {activeTab}… Previously loaded entries may remain visible.
            </p>
          )}
          {activityError && (
            <div className="card full executor-detail49__activity-error" role="alert">
              <p>{activityError}</p>
              <p>Previously loaded entries may be out of date. Open another section, then return to try again. Refresh status only reloads executor status.</p>
            </div>
          )}
        </>
      )}

      {node && activeTab === 'overview' && (
        <>
          <div className="card full">
            <div className="card-header">
              <h3>Runtime and ownership</h3>
              <div className="row">
                <span className={statusClass(statusLabel(node, heartbeatFreshFor(node)))}>
                  {statusLabel(node, heartbeatFreshFor(node))}
                </span>
                <span className={runtimeStatus?.className}>{runtimeStatus?.label}</span>
              </div>
            </div>
            <div className="detail-grid">
              <div>
                <span className="config-label">Executor ID</span>
                <p className="mono">{node.id}</p>
              </div>
              <div>
                <span className="config-label">Owner</span>
                <p>{executorOwnerLabel(node)}</p>
                {node.owner?.name?.trim() && node.owner.email?.trim() && (
                  <p className="hint">{node.owner.email}</p>
                )}
              </div>
              <div>
                <span className="config-label">Tags</span>
                <p>{node.tags.length ? node.tags.map((tag) => `#${tag}`).join(' ') : '-'}</p>
              </div>
              <div>
                <span className="config-label">Assurance</span>
                <p>{node.assurance_class.replaceAll('_', ' ')}</p>
                <p className="hint">{node.assurance_state}</p>
              </div>
              <div>
                <span className="config-label">Last seen</span>
                <p>{formatTimestamp(node.last_seen_at)}</p>
                <p className={heartbeatFreshFor(node) ? 'hint' : 'warning-text'}>{heartbeatHintFor(node)}</p>
              </div>
              <div>
                <span className="config-label">Current job</span>
                <p className="mono">{node.current_job_id || '-'}</p>
              </div>
              <div>
                <span className="config-label">Runner</span>
                <p className="mono">{node.capabilities?.runner_git_hash || 'unknown'}</p>
              </div>
              <div>
                <span className="config-label">Backend</span>
                <p className="mono">{node.backend_git_hash || 'unknown'}</p>
              </div>
            </div>

            {!heartbeatFreshFor(node) && (
              <p className="warning-text">
                This executor has no fresh heartbeat. Queued helpers and
                self-updates will remain pending until the executor reconnects.
              </p>
            )}

            {node.self_update && (
              <details className="worker-checks" open={selfUpdateActive(node) || node.self_update.status === 'failed'}>
                <summary>
                  <span className={helperStatusClass(node.self_update.status)}>
                    self-update {node.self_update.status}
                  </span>
                </summary>
                <div className="stack stack--compact">
                  <span className="hint">queued {formatTimestamp(node.self_update.created_at)}</span>
                  {node.self_update.leased_at && <span className="hint">started {formatTimestamp(node.self_update.leased_at)}</span>}
                  {node.self_update.finished_at && <span className="hint">finished {formatTimestamp(node.self_update.finished_at)}</span>}
                  {node.self_update.last_error && <span className="warning-text">{node.self_update.last_error}</span>}
                </div>
              </details>
            )}
            {node.self_update?.status?.toLowerCase() === 'succeeded' && !node.runner_current && (
              <p className="warning-text">
                {awaitingReconnect
                  ? 'The update finished, but this executor has not re-registered with the new runner yet.'
                  : 'The last self-update succeeded, but this executor still reports an older runner.'}
              </p>
            )}
          </div>

          {canManageNode && (
            <div className="card full">
              <div className="card-header">
                <h3>Executor controls</h3>
                <span className="badge">Operator</span>
              </div>
              <div className="executor-detail-grid">
                <label>
                  Display name
                  <div className="row row--compact">
                    <input
                      type="text"
                      value={name}
                      onChange={(event) => {
                        nameDirty.current = true;
                        setName(event.target.value);
                      }}
                      placeholder={node.id}
                    />
                    <button type="button" onClick={() => void saveName()} disabled={saving}>
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </label>
                <label className="checkbox-row executor-toggle-row">
                  <input
                    type="checkbox"
                    checked={node.conversation_enabled}
                    disabled={saving || !node.enabled}
                    onChange={(event) =>
                      void updateNode({ conversation_enabled: event.target.checked })
                    }
                  />
                  <span>
                    Accept conversation work
                    <span className="hint">
                      Drain agent messages while keeping approved operational helpers available.
                    </span>
                  </span>
                </label>
                <label className="checkbox-row executor-toggle-row">
                  <input
                    type="checkbox"
                    checked={node.enabled}
                    disabled={saving}
                    onChange={(event) => void updateNode({ enabled: event.target.checked })}
                  />
                  <span>
                    Executor enabled
                    <span className="hint">Stop all new leases when disabled.</span>
                  </span>
                </label>
                <label className="checkbox-row executor-toggle-row">
                  <input
                    type="checkbox"
                    checked={node.runtime_validation_trusted}
                    disabled={saving}
                    onChange={(event) =>
                      void updateRuntimeValidationTrust(event.target.checked)
                    }
                  />
                  <span>
                    Trusted Firecracker QA token
                    <span className="hint">
                      Applies to every executor enrolled with token {node.token_id}.
                    </span>
                  </span>
                </label>
                <label>
                  QA runtime validation priority
                  <div className="row row--compact">
                    <input
                      type="number"
                      min={1}
                      max={9999}
                      value={qaPriority}
                      disabled={saving}
                      onChange={(event) => {
                        qaPriorityDirty.current = true;
                        setQaPriority(event.target.value);
                      }}
                      placeholder="not set"
                    />
                    <button type="button" onClick={() => void saveQaPriority()} disabled={saving}>
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                  <span className="hint">
                    Ordered failover among trusted Firecracker QA executors; lower number wins.
                    Blank leaves this executor unordered.
                  </span>
                </label>
              </div>
            </div>
          )}

          {canManageNode && (
            <div className="card full stack">
              <div className="card-header">
                <div>
                  <h3>Create independent stage executor</h3>
                  <p className="status">
                    Creates a new token, executor identity, service, and state paths.
                  </p>
                </div>
              </div>
              <div className="detail-grid">
                <label>
                  Target stage
                  <input
                    value={cloneStage}
                    onChange={(event) => setCloneStage(event.target.value)}
                    placeholder="production"
                  />
                </label>
                <label>
                  Instance name
                  <input
                    value={cloneInstance}
                    onChange={(event) => setCloneInstance(event.target.value)}
                    placeholder="yml-prod-02"
                  />
                </label>
              </div>
              <button
                type="button"
                disabled={saving || !cloneStage.trim() || !cloneInstance.trim()}
                onClick={() => void createClonePlan()}
              >
                Create independent enrollment plan
              </button>
              {clonePlan && (
                <div className="stack">
                  <p className="warning-text">
                    This enrollment token is shown once and expires{' '}
                    {formatTimestamp(clonePlan.enrollment_token_expires_at)}.
                  </p>
                  <label>
                    Enrollment token
                    <input className="mono" readOnly value={clonePlan.enrollment_token} />
                  </label>
                  <pre className="log-box">{clonePlan.install_command}</pre>
                  <pre className="log-box">
                    {JSON.stringify(clonePlan.install_paths, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}

          <div className="card full">
            <div className="card-header">
              <h3>Capabilities</h3>
              <span className={capabilityEnabled(node.capabilities?.firecracker) ? 'badge badge--success' : 'badge badge--warning'}>
                {capabilityEnabled(node.capabilities?.firecracker) ? 'Firecracker ready' : 'No Firecracker'}
              </span>
            </div>
            <pre className="log-box">{JSON.stringify(node.capabilities, null, 2)}</pre>
          </div>
        </>
      )}

      {node && activeTab === 'identities' && canManageTerminalIdentities && (
        <ExecutorTerminalIdentitiesPanel apiClient={apiClient} executor={node} setError={setError} />
      )}

      {node && activeTab === 'operations' && canManageNode && (
        <>
          <div className="card full">
            <div className="card-header">
              <div>
                <h3>Approved operations</h3>
                <p className="status">
                  Operations use the controlled helper catalog and executor grant model.
                </p>
              </div>
              {activityLoading && <span className="badge badge--processing">Loading</span>}
            </div>
            {operations?.operations.some((operation) => operation.id === 'self_update') ? (
              <div className="executor-operation-row">
                <div>
                  <strong>Update runner</strong>
                  <p className="status">
                    Drain conversation work and apply the signed runner update.
                  </p>
                </div>
                <div className="row">
                  <button
                    type="button"
                    disabled={
                      updating ||
                      !node.enabled ||
                      selfUpdateActive(node)
                    }
                    onClick={() => void drainAndUpdate()}
                  >
                    {updating ? 'Queuing...' : 'Drain and update'}
                  </button>
                  <button
                    type="button"
                    className="secondary"
                    disabled={
                      updating ||
                      !node.enabled ||
                      selfUpdateActive(node)
                    }
                    onClick={() => void forceUpdate()}
                  >
                    Queue without drain
                  </button>
                </div>
              </div>
            ) : (
              !activityLoading && !activityError && <p className="status">No approved operations are available.</p>
            )}
            <p className="hint">
              Arbitrary host commands are not accepted here. Catalog approval and execution
              grants remain required.
            </p>
          </div>
          <div className="card full">
            <div className="card-header">
              <div>
                <h3>Operation history</h3>
                <p className="status">
                  Sensitive helper payloads and output are not displayed.
                </p>
              </div>
              <span className="badge">{node.helper_requests?.length || 0} recent</span>
            </div>
            {node.helper_requests?.length ? (
              <div className="table-scroll" role="region" aria-label="Operation history table, scroll horizontally for more columns" tabIndex={0}>
                <table>
                  <thead>
                    <tr>
                      <th scope="col">Status</th>
                      <th scope="col">Request</th>
                      <th scope="col">Queued</th>
                      <th scope="col">Started</th>
                      <th scope="col">Finished</th>
                      <th scope="col">Duration</th>
                      <th scope="col">Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {node.helper_requests.map((request) => (
                      <tr key={request.id}>
                        <td>
                          <span className={helperStatusClass(request.status)}>
                            {request.status}
                          </span>
                        </td>
                        <td>
                          {request.helper}
                          <br />
                          <span className="hint mono">{request.id}</span>
                        </td>
                        <td>{formatTimestamp(request.created_at)}</td>
                        <td>{formatTimestamp(request.leased_at)}</td>
                        <td>{formatTimestamp(request.finished_at)}</td>
                        <td>
                          {requestDuration(
                            request.created_at,
                            request.leased_at,
                            request.finished_at
                          )}
                        </td>
                        <td>
                          {request.last_error ? (
                            <span className="warning-text">{request.last_error}</span>
                          ) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="status">No executor requests have been recorded.</p>
            )}
          </div>
        </>
      )}

      {node && activeTab === 'workloads' && (
        <div className="card full">
          <div className="card-header">
            <div>
              <h3>Workloads</h3>
              <p className="status">Recent runs leased to this executor.</p>
            </div>
            {activityLoading && <span className="badge badge--processing">Loading</span>}
          </div>
          {workloads.length ? (
            <div className="table-scroll" role="region" aria-label="Workloads table, scroll horizontally for more columns" tabIndex={0}>
              <table>
                <thead>
                  <tr>
                    <th scope="col">Status</th>
                    <th scope="col">Conversation</th>
                    <th scope="col">Agent</th>
                    <th scope="col">Created</th>
                    <th scope="col">Finished</th>
                  </tr>
                </thead>
                <tbody>
                  {workloads.map((workload) => (
                    <tr key={workload.run_id}>
                      <td><span className={helperStatusClass(workload.status)}>{workload.status}</span></td>
                      <td>
                        <Link to={workload.link}>
                          {workload.conversation_title || workload.conversation_id}
                        </Link>
                        <br />
                        <span className="hint mono">{workload.run_id}</span>
                      </td>
                      <td>{workload.agent_name || workload.kind || '-'}</td>
                      <td>{formatTimestamp(workload.created_at)}</td>
                      <td>{formatTimestamp(workload.finished_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            !activityLoading && !activityError && <p className="status">No attributed workloads yet.</p>
          )}
        </div>
      )}

      {node && activeTab === 'events' && (
        <div className="card full">
          <div className="card-header">
            <div>
              <h3>Events</h3>
              <p className="status">
                Audit records are sanitized according to your executor permissions.
              </p>
            </div>
            {activityLoading && <span className="badge badge--processing">Loading</span>}
          </div>
          {events.length ? (
            <div className="table-scroll" role="region" aria-label="Events table, scroll horizontally for more columns" tabIndex={0}>
              <table>
                <thead>
                  <tr>
                    <th scope="col">Time</th>
                    <th scope="col">Action</th>
                    <th scope="col">Actor</th>
                    <th scope="col">Context</th>
                    <th scope="col">Metadata</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event) => (
                    <tr key={event.id}>
                      <td>{formatTimestamp(event.created_at)}</td>
                      <td>{event.action}</td>
                      <td>{event.actor_kind}</td>
                      <td>
                        {event.link ? (
                          <Link to={event.link}>Conversation</Link>
                        ) : event.session_id ? (
                          <span className="mono">{event.session_id}</span>
                        ) : '-'}
                      </td>
                      <td><code>{JSON.stringify(event.metadata)}</code></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            !activityLoading && !activityError && <p className="status">No retained executor events.</p>
          )}
        </div>
      )}
    </section>
  );
}
